/**
 * A saved custom-attribute value as this screen holds it. The API returns `{ colId, valueId,
 * valueText }` with `colId` either populated or a bare id; `handleSelectDynamic` rebuilds entries
 * from the attribute master, where the attribute id sits on `_id`. Both shapes are part of the
 * contract, so the attribute id is resolved rather than read off one field.
 */
export interface DynamicColumnEntry {
  _id?: unknown;
  colId?: { _id?: unknown } | string | null;
  [key: string]: unknown;
}

/**
 * The custom-attribute id an entry belongs to, whichever shape it arrived in.
 * @param entry a stored dynamicColumn entry
 * @returns the attribute id, or an empty string when none can be read
 */
export const entryAttributeId = (entry: DynamicColumnEntry | null | undefined): string => {
  if (!entry) return '';
  if (entry._id) return String(entry._id);
  if (entry.colId && typeof entry.colId === 'object' && entry.colId._id) return String(entry.colId._id);
  if (typeof entry.colId === 'string') return entry.colId;
  return '';
};

/**
 * Drop stored values for attributes the product's new brand + product type no longer admit.
 *
 * Run only after the user changes brand or product type: the scoped attribute list is refetched
 * for the new pair, and any value whose attribute fell out of it belongs to the old pair. A plain
 * open-and-save never comes through here, so a value whose attribute was merely re-scoped in
 * System Masters is left alone — that is the case the save-time prune was narrowed to protect.
 *
 * @param dynamicColumn the product's stored values
 * @param inScopeIds ids of the attributes the refetched scope returned
 * @returns the values still in scope, in their original order
 */
export const pruneOutOfScopeDynamicColumn = <T extends DynamicColumnEntry>(dynamicColumn: T[] | null | undefined, inScopeIds: Iterable<unknown>): T[] => {
  if (!Array.isArray(dynamicColumn) || !dynamicColumn.length) return [];
  const admitted = new Set(Array.from(inScopeIds, String));
  return dynamicColumn.filter((entry) => admitted.has(entryAttributeId(entry)));
};

/** A multi-select custom attribute definition, as far as its product type scope goes. */
export interface MultiCustomAttributeMaster {
  _id?: unknown;
  productType?: unknown[] | null;
}

/** A stored multi-select custom attribute value: which attribute it belongs to, and its chosen options. */
export interface MultiCustomAttributeValue {
  colId?: unknown;
}

/**
 * Whether a multi-select attribute is offered for a product type. Mirrors the screen's own rule for
 * showing the attribute: it must list the type, and a product with no type is offered none.
 * @param attribute the attribute definition
 * @param productTypeId the product's type id, or an empty string when it has none
 * @returns true when the attribute lists the type
 */
export const multiAttributeAdmits = (attribute: MultiCustomAttributeMaster, productTypeId: string): boolean =>
  Boolean(productTypeId) &&
  Array.isArray(attribute.productType) &&
  attribute.productType.some((scoped) => String((scoped as { _id?: unknown } | null)?._id ?? scoped) === productTypeId);

/**
 * Drop stored multi-select values whose attribute the product's new type no longer offers.
 * @param entries the product's stored values
 * @param master every multi-select attribute definition
 * @param productTypeId the product's new type id, or an empty string when it has none
 * @returns the values still offered, in their original order
 */
export const pruneOutOfScopeMultiCustomAttribute = <T extends MultiCustomAttributeValue>(
  entries: T[] | null | undefined,
  master: ReadonlyArray<MultiCustomAttributeMaster>,
  productTypeId: string,
): T[] => {
  if (!Array.isArray(entries) || !entries.length) return [];
  const admitted = new Set(master.filter((attribute) => multiAttributeAdmits(attribute, productTypeId)).map((attribute) => String(attribute._id)));
  return entries.filter((entry) => admitted.has(String(entry.colId ?? '')));
};
